import { Flex, Text, Button, Tooltip, Link } from '@chakra-ui/react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useGetPassQuery } from '../../../store/redux/api/passApi';
import PageHeadingWithTrialProgress from '../dashboard/PageHeadingWithTrialProgress';
import type { PassContent } from '../../../store/types/pass';
import { truncate } from './utils/passHelpers';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

const hasLocalisation = (content: PassContent | undefined): boolean => {
  if (!content || typeof content !== 'object') return false;
  return Object.values(content).some((field) => {
    return (
      field &&
      typeof field === 'object' &&
      (field as { localisation?: Record<string, string> }).localisation &&
      Object.keys(
        (field as { localisation: Record<string, string> }).localisation
      ).length > 0
    );
  });
};

const PassesViewHeader = ({
  isEdit,
  isCreate,
}: {
  isEdit?: boolean;
  isCreate?: boolean;
}) => {
  const { uuid } = useParams<{ uuid: string }>();
  const { data: passData } = useGetPassQuery(
    { uuid: uuid ?? '' },
    { skip: !uuid }
  );

  const isReadOnly = useIsReadOnly();
  const pass = passData?.data?.data;
  const template = passData?.meta?.template;
  const passHasLocalisation = hasLocalisation(pass?.attributes?.passContent);

  const canEdit =
    (pass?.attributes?.status === 'active' ||
      pass?.attributes?.status === 'issued' ||
      pass?.attributes?.status === 'pending') &&
    !isEdit;

  return (
    <>
      <PageHeadingWithTrialProgress
        title={
          isEdit
            ? `Edit ${template?.name} pass`
            : isCreate
              ? `Create new pass`
              : `${template?.name} pass`
        }
        displayTrialProgress={false}
      />
      {!isCreate &&
        (() => {
          const extId = pass?.attributes?.extId;
          const fullId = extId || uuid;
          const label = extId ? 'Ext ID' : 'UUID';
          const displayText = truncate(fullId, 80);
          const isTruncated = fullId !== displayText;
          return (
            <Tooltip
              label={fullId}
              placement="bottom-start"
              openDelay={300}
              fontSize="12px"
              borderRadius="5px"
              textAlign="left"
              p={2}
              isDisabled={!isTruncated}
            >
              <Text fontSize="18px" color="#989898" mt="-8px" mb="10px">
                {label}: {displayText}
              </Text>
            </Tooltip>
          );
        })()}
      <Flex
        w="full"
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        {isEdit ? (
          <Flex direction="row" alignItems="center" gap="4px">
            <Text textStyle="bodyMedium" my="10px">
              Changes here will affect this individual pass. To update the
              design or fields for all passes, edit the
            </Text>
            <Link
              as={RouterLink}
              to={`/pass-templates/edit/${template?.uuid}`}
              color="brand"
              textDecoration="underline"
              textUnderlineOffset="3px"
            >
              pass template.
            </Link>
          </Flex>
        ) : isCreate ? (
          <Text textStyle="bodyMedium" my="10px">
            Choose a template to issue a pass from. The pass will inherit the
            template's design and fields, which you can override for this
            individual pass if needed.
          </Text>
        ) : (
          <Text textStyle="bodyMedium" my="10px">
            Distribute this pass, track its activity and manage its status.
          </Text>
        )}
        {canEdit &&
          (isReadOnly ? (
            <LockedActionButton size="sm" px="20px" py="12px">
              Edit pass
            </LockedActionButton>
          ) : (
            <Button
              as={RouterLink}
              to={`/passes/edit/${pass?.id}`}
              size="sm"
              px="20px"
              py="12px"
            >
              Edit pass
            </Button>
          ))}
      </Flex>
    </>
  );
};
export default PassesViewHeader;
